import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, Dialog, Button, Switch, toast } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { CheckCheck, RefreshCw } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { refusalMessage, revisionLabel, RoutingClient, type RoutingReviewFlag, type RoutingReviewRowDTO } from "../../api/manufacturing";
import { OpenRecord } from "../../components/OpenRecord";
import { NexpimView } from "../../views";

function flagLabel(t: TFunction, flag: RoutingReviewFlag): string {
  return {
    NO_ROUTING: t("routing-review.flag.no-routing", "No routing"),
    INSPECTION_ONLY: t("routing-review.flag.inspection-only", "Inspection only"),
    UNKNOWN_MACHINE: t("routing-review.flag.unknown-machine", "Unknown machine"),
    NO_DEPENDENCY: t("routing-review.flag.no-dependency", "Waits for nothing"),
    NO_MACHINE: t("routing-review.flag.no-machine", "No CNC"),
  }[flag];
}

function flagHint(t: TFunction, flag: RoutingReviewFlag, row: RoutingReviewRowDTO): string {
  return {
    NO_ROUTING: t("routing-review.hint.no-routing", "No steps: open the part and keep its proposed routing, or write one."),
    INSPECTION_ONLY: t("routing-review.hint.inspection-only", "Only an Inspection step: the part has no size or machines on file."),
    UNKNOWN_MACHINE: t("routing-review.hint.unknown-machine", "Words of SIGM's machine list that named no machine: {{words}}", {
      words: row.unknown_tokens.join(", "),
    }),
    NO_DEPENDENCY: t("routing-review.hint.no-dependency", "A step past the first stage waits for nothing: approval refuses it."),
    NO_MACHINE: t("routing-review.hint.no-machine", "A machining step allows no CNC: say which machines run its program."),
  }[flag];
}

/**
 * Every floor-tracked part's routing, to take the routings read from SIGM to
 * approval: a lot is released only on approved routings.
 *
 * <p>Parts the plant makes (on a lot in the last 24 months, or an open one)
 * come first and are the only ones shown by default; drafts before approved,
 * flagged before clean. Clean drafts can be approved several at a time; the
 * server checks each and says why it refused any.</p>
 */
export function RoutingReviewPage() {
  const { t } = useTranslation();
  const { http, hasView, canWrite } = useAuth();
  const queryClient = useQueryClient();
  const allowed = hasView(NexpimView.MANUFACTURING_PARTS);
  const routings = useMemo(() => new RoutingClient(http), [http]);
  const [madeOnly, setMadeOnly] = useState(true);
  const [selected, setSelected] = useState<RoutingReviewRowDTO[]>([]);
  const [refused, setRefused] = useState<Array<{ row: RoutingReviewRowDTO | undefined; reason: string }> | null>(null);

  const review = useQuery({
    queryKey: ["manufacturing", "routing", "review"],
    queryFn: () => routings.review(),
    enabled: allowed,
  });
  const all = review.data ?? [];
  const rows = madeOnly ? all.filter((row) => row.recently_made) : all;
  const counts = useMemo(() => {
    const made = all.filter((row) => row.recently_made);
    return {
      approved: made.filter((row) => row.routing_status === "APPROVED").length,
      draft: made.filter((row) => row.routing_status === "DRAFT").length,
      none: made.filter((row) => row.routing_status == null).length,
    };
  }, [all]);

  const approve = useMutation({
    mutationFn: (bomIds: number[]) => routings.approveAll(bomIds),
    onSuccess: async (refusals, bomIds) => {
      await queryClient.invalidateQueries({ queryKey: ["manufacturing", "routing", "review"] });
      await queryClient.invalidateQueries({ queryKey: ["manufacturing", "bom"] });
      const entries = Object.entries(refusals);
      if (entries.length > 0) {
        setRefused(entries.map(([bomId, reason]) => ({ row: all.find((row) => row.bom_id === Number(bomId)), reason })));
      }
      toast.success(t("routing-review.approved", "{{n}} routings approved", { n: bomIds.length - entries.length }));
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });
  const approvable = selected.filter((row) => row.bom_id != null && row.routing_status === "DRAFT");

  const cols = useMemo(() => {
    const c = defineColumns<RoutingReviewRowDTO>();
    return [
      c.custom(
        "part_number",
        (row) => row.part_number,
        (row) => (
          <span className="flex items-center gap-2">
            <span className="font-medium">{row.part_number}</span>
            <OpenRecord target={{ kind: "part", id: row.part_id, tab: "bom" }} label={row.part_number} />
          </span>
        ),
        { headerKey: "column.part.number", priority: 1 },
      ),
      c.text("part_name", (row) => row.part_name ?? "", { headerKey: "column.name", priority: 2 }),
      c.custom(
        "routing_status",
        (row) => row.routing_status ?? "",
        (row) =>
          row.routing_status === "APPROVED" ? (
            <Badge variant="success" size="sm">
              {t("routing.status.approved", "Approved")}
            </Badge>
          ) : row.routing_status === "DRAFT" ? (
            <Badge variant="warning" size="sm">
              {t("routing.status.draft", "Draft")}
            </Badge>
          ) : (
            <Badge variant="error" size="sm">
              {t("routing-review.flag.no-routing", "No routing")}
            </Badge>
          ),
        { header: t("routing-review.status", "Routing"), filter: "select", priority: 1 },
      ),
      c.text("steps", (row) => row.steps, { header: t("routing-review.steps", "Steps"), priority: 1 }),
      c.custom(
        "flags",
        (row) => row.flags.join(" "),
        (row) => (
          <span className="flex flex-wrap gap-1">
            {row.flags
              .filter((flag) => flag !== "NO_ROUTING")
              .map((flag) => (
                <span key={flag} title={flagHint(t, flag, row)}>
                  <Badge variant="warning" size="sm">
                    {flagLabel(t, flag)}
                  </Badge>
                </span>
              ))}
          </span>
        ),
        { header: t("routing-review.flags", "To look at"), priority: 2 },
      ),
      c.text("derived", (row) => (row.derived ? t("routing-review.derived", "From SIGM's text") : ""), {
        header: t("routing-review.origin", "Origin"),
        filter: "select",
        priority: 3,
      }),
      c.text("revision", (row) => (row.revision == null ? "" : revisionLabel(row.revision_at, row.revision)), { header: t("routing-review.revision", "Revision"), priority: 4 }),
    ];
  }, [t]);

  if (!allowed) {
    return (
      <>
        <PageHeader title={t("navigation.item.routings", "Routings")} />
        <p className="p-6 text-[13px] text-ink-muted">{t("error.access-denied.detail", "Your account does not have permission to view this page.")}</p>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t("navigation.item.routings", "Routings")}
        subtitle={t("routing-review.subtitle", "Every part's steps, to check and approve: a lot is released only on approved routings.")}
        badges={
          <>
            <Badge variant="success" size="sm">
              {t("routing-review.count.approved", "{{n}} approved", { n: counts.approved })}
            </Badge>
            <Badge variant="warning" size="sm">
              {t("routing-review.count.draft", "{{n}} drafts", { n: counts.draft })}
            </Badge>
            {counts.none > 0 && (
              <Badge variant="error" size="sm">
                {t("routing-review.count.none", "{{n}} without", { n: counts.none })}
              </Badge>
            )}
          </>
        }
      />
      <DataTable<RoutingReviewRowDTO>
        data={rows}
        columns={cols}
        getRowId={(row) => String(row.part_id)}
        loading={review.isLoading}
        selectable={canWrite}
        onSelectionChange={setSelected}
        toolbarExtra={<Switch checked={madeOnly} onChange={setMadeOnly} label={t("routing-review.made-only", "Parts the plant makes")} />}
        actions={[
          {
            label: t("routing-review.approve-selected", "Approve selected ({{n}})", { n: approvable.length }),
            icon: <CheckCheck className="size-4" />,
            write: true,
            disabled: approvable.length === 0 || approve.isPending,
            onClick: () => approve.mutate(approvable.map((row) => row.bom_id as number)),
          },
          {
            label: t("menu.item.refresh", "Refresh"),
            icon: <RefreshCw className="size-4" />,
            onClick: () => void review.refetch(),
          },
        ]}
        emptyMessage={t("routing-review.empty", "No floor-tracked parts.")}
      />
      {refused && (
        <Dialog
          open
          onClose={() => setRefused(null)}
          title={t("routing-review.refused.title", "Not approved")}
          description={t("routing-review.refused.description", "These routings did not pass their checks; open each part to fix it.")}
          actions={
            <Button variant="primary" onClick={() => setRefused(null)}>
              {t("dialog.close", "Close")}
            </Button>
          }
        >
          <ul className="flex max-h-80 flex-col gap-2 overflow-auto text-[13px]">
            {refused.map(({ row, reason }, index) => (
              <li key={row?.part_id ?? index} className="flex flex-col gap-0.5">
                <span className="flex items-center gap-2 font-medium">
                  {row?.part_number}
                  {row && <OpenRecord target={{ kind: "part", id: row.part_id, tab: "bom" }} label={row.part_number} />}
                </span>
                <span className="text-ink-muted">{reason}</span>
              </li>
            ))}
          </ul>
        </Dialog>
      )}
    </>
  );
}
